import React from "react"
import { useEffect, useState } from "react"
import SectionBackground from "../../molecules/SectionBackground/SectionBackground"
import Image from "../../atoms/Image/Image"
import { css, keyframes } from "@emotion/react"

/*
 * Intro estilo 1 (campo "Estilo" = Texto e imagenes en el ACF de Inicio).
 *
 * ── Línea de tiempo del intro (segundos) ──
 *
 * Fase 1 — escritura (máquina de escribir, sin cursor)
 *   0.08 – 0.98   Línea 1 a 28ms por carácter. Los espacios también consumen
 *                 su turno. El ancho final queda reservado, así que la primera
 *                 letra nace donde quedará el borde izquierdo de la frase y el
 *                 texto solo crece hacia la derecha.
 *   0.98 – 1.36   Pausa con la línea 1 completa
 *   1.36 – 1.68   Aparece sola la "M" de la línea 2
 *   1.68 – 2.46   Resto de la línea 2 a 60ms por carácter. Su caja mide lo que
 *                 medirá la frase completa y va centrada, así que no se mueve
 *                 ni cambia de ancho: dentro de ella cada letra nueva nace en
 *                 el borde derecho y empuja a las anteriores hacia la izquierda.
 *   2.46 – 3.20   Las dos líneas quietas
 *
 * Fase 2 — salida
 *   3.20 – 3.60   Las dos líneas se desvanecen juntas, opacidad pura
 *   3.60 – 3.76   Pantalla vacía
 *
 * Fase 3 — ilustración
 *   3.76 – 5.16   Se monta el WebP: la silueta sube de opacidad y después la
 *                 línea de los bucles se traza hasta la mano levantada. La
 *                 animación vive dentro del archivo (35 cuadros de 40ms, sin
 *                 loop), por eso acá solo se controla cuándo entra al DOM.
 *   5.16 – 6.00   El dibujo queda fijo y completo
 *   6.00 – 6.40   El dibujo se desvanece en su sitio, igual que el texto
 *   6.40 – 7.40   Colapso de la sección (ya vacía) y desbloqueo del scroll
 *
 * Los segundos de arriba los cuenta el CSS, que arranca con el primer pintado
 * (no con la hidratación de React). Los momentos que necesitan JS —las letras
 * de la línea 2, el montaje del dibujo y soltar el scroll— leen ese mismo
 * reloj en cada frame, así que nunca se van de sincronía; ver el useEffect.
 */

const LINEA_1 = "Hoy será el mejor día de tu vida."
const LINEA_2 = "Mañana también."

/*
 * La paleta va fija y no desde el CMS: el trazo del dibujo viene quemado en el
 * WebP (#946B31), así que solo funciona sobre este amarillo. Los campos
 * colorFondoIntro / colorTextoIntro de WordPress quedan sin efecto en esta
 * sección; para volver a mandarlos desde el CMS habría que exportar el dibujo
 * como SVG y pintar el stroke con currentColor.
 */
const COLOR_FONDO = "#F3C668"
const COLOR_TEXTO = "#95692B"

const LINEA_1_INICIO = 0.08
const LINEA_1_PASO = 0.028
const LINEA_2_PRIMERA = 1.36
const LINEA_2_RESTO = 1.68
const LINEA_2_PASO = 0.06

const TEXTO_SALIDA = 3.2
const TEXTO_SALIDA_DURACION = 0.4

const DIBUJO_INICIO = 3.76
const DIBUJO_SALIDA = 6.0
const DIBUJO_SALIDA_DURACION = 0.4

/*
 * El colapso arranca recién cuando el dibujo ya se apagó. Si se solaparan, la
 * ilustración iría centrada dentro de una caja que se encoge y su centro subiría
 * hasta el borde superior: se vería resbalar hacia arriba mientras se va.
 */
const SECTION_EXIT = 6.4
const SECTION_EXIT_DURATION = 1.0
const INTRO_TOTAL = SECTION_EXIT + SECTION_EXIT_DURATION

/*
 * El WebP viene recortado a su contenido real (555x491), sin el aire
 * transparente del GIF original, así que el ancho de aquí es el ancho del
 * dibujo. El still es el último cuadro, para prefers-reduced-motion.
 */
const DIBUJO_SRC = "/armandito.webp"
const DIBUJO_STILL_SRC = "/armandito-final.webp"
const DIBUJO_ANCHO = 555

/*
 * Línea 1 agrupada por palabra: el salto de línea en móvil cae entre palabras y
 * no entre letras. Cada letra guarda su retraso según su índice global, porque
 * los espacios también cuentan aunque no se animen (son invisibles).
 */
const palabrasLinea1 = []
let indiceLinea1 = 0
LINEA_1.split(" ").forEach(palabra => {
  palabrasLinea1.push(
    palabra.split("").map(letra => {
      const delay = LINEA_1_INICIO + indiceLinea1 * LINEA_1_PASO
      indiceLinea1 += 1
      return [letra, delay]
    })
  )
  indiceLinea1 += 1
})

/* Retraso de cada letra de la línea 2: la "M" sola y después el resto */
const retrasosLinea2 = LINEA_2.split("").map((_, index) =>
  index === 0 ? LINEA_2_PRIMERA : LINEA_2_RESTO + (index - 1) * LINEA_2_PASO
)

/*
 * El espacio va como no-breaking: un espacio normal al final del renglón lo
 * colapsa el navegador, y al estar la línea pegada a la derecha eso hacía que
 * el texto se quedara quieto un turno y después saltara dos caracteres juntos.
 */
const recorteLinea2 = n => LINEA_2.slice(0, n).replace(/ /g, "\u00A0")

export default function IntroTextoInicio({
  imagen,
  imagenMobile,
  colorFondo,
  colorTexto,
  titulo,
  autor,
}) {
  /* colorFondo / colorTexto llegan del CMS pero no se usan: ver COLOR_FONDO */
  const [letrasLinea2, setLetrasLinea2] = useState(0)
  const [mostrarDibujo, setMostrarDibujo] = useState(false)
  const [sinMovimiento, setSinMovimiento] = useState(false)

  useEffect(() => {
    const bodyHtml = document.querySelector("body")
    const seccion = document.querySelector(".sectionintro")

    /* prefers-reduced-motion: se muestra el estado final, sin animar ni bloquear */
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setSinMovimiento(true)
      return undefined
    }

    /*
     * El reloj lo lleva el CSS, no el JS: las animaciones de las letras, del
     * fundido y del colapso arrancan con el primer pintado (en producción el
     * HTML viene del SSR, así que empiezan antes de que hidrate React).
     *
     * Los momentos que sí necesitan JS se leen de ese mismo reloj en cada
     * frame en vez de agendarse con setTimeout. La diferencia importa cuando
     * la pestaña está en segundo plano: ahí el navegador congela el reloj de
     * las animaciones y el de requestAnimationFrame, pero los setTimeout
     * siguen disparando. Con timers, alguien que abre la web en una pestaña
     * de fondo volvía y encontraba la línea 2 ya escrita y el dibujo puesto
     * sobre un texto que recién se estaba tecleando.
     */
    const animacionSeccion =
      seccion && seccion.getAnimations && seccion.getAnimations()[0]
    const arranque = performance.now()
    const reloj = () =>
      animacionSeccion
        ? Math.max(0, animacionSeccion.currentTime || 0)
        : performance.now() - arranque

    /* Si el intro ya terminó antes de hidratar, no tiene sentido bloquear */
    if (reloj() < INTRO_TOTAL * 1000) {
      bodyHtml.classList.add("no-scroll")
    }

    /*
     * Precarga fuera del DOM: descarga el WebP sin renderizarlo, así que su
     * animación no avanza. Al montar el <img> más tarde, sale de caché y
     * arranca desde el primer cuadro.
     */
    const precarga = new window.Image()
    precarga.src = DIBUJO_SRC

    /*
     * La línea 2 se corta con JS en vez de animar opacidades: al montar solo
     * las letras ya escritas, la línea centrada se reacomoda sola en cada
     * carácter. La línea 1, en cambio, es CSS puro para que el ancho quede
     * reservado desde el inicio y no salte el layout.
     */
    const hitos = [
      ...retrasosLinea2.map((delay, index) => [
        delay * 1000,
        () => setLetrasLinea2(index + 1),
      ]),
      [DIBUJO_INICIO * 1000, () => setMostrarDibujo(true)],
      [INTRO_TOTAL * 1000, () => bodyHtml.classList.remove("no-scroll")],
    ].sort((a, b) => a[0] - b[0])

    let pendiente = 0
    let frameID = null

    const revisar = () => {
      const ahora = reloj()
      while (pendiente < hitos.length && hitos[pendiente][0] <= ahora) {
        hitos[pendiente][1]()
        pendiente += 1
      }
      if (pendiente < hitos.length) frameID = requestAnimationFrame(revisar)
    }

    revisar()

    return () => {
      bodyHtml.classList.remove("no-scroll")
      if (frameID !== null) cancelAnimationFrame(frameID)
    }
  }, [])

  /* ── Keyframes ── */

  const sectionAnim = keyframes`
    from {
      opacity: 1;
      height: 100vh;
    }
    to {
      opacity: 0;
      height: 0vh;
    }
  `

  const fadeIn = keyframes`
    from { opacity: 0; }
    to   { opacity: 1; }
  `

  const fadeOut = keyframes`
    from { opacity: 1; }
    to   { opacity: 0; }
  `

  /* ── Section ── */

  const sectionCss = css`
    height: 100vh;
    position: relative;
    inset: 0;
    z-index: 999;
    pointer-events: none;
    /* Nada se asoma fuera de la caja mientras colapsa */
    overflow: hidden;
    animation-name: ${sectionAnim};
    animation-duration: ${SECTION_EXIT_DURATION}s;
    animation-delay: ${SECTION_EXIT}s;
    animation-iteration-count: 1;
    animation-direction: normal;
    animation-timing-function: ease;
    animation-fill-mode: forwards;
    @media (prefers-reduced-motion: reduce) {
      animation: none;
    }
  `

  const sectionContentCss = css`
    position: relative;
    height: 100%;
  `

  /* ── Texto – bloque centrado en el tercio superior ── */

  const textWrapperCss = css`
    position: absolute;
    top: 24%;
    left: 0;
    right: 0;
    display: flex;
    justify-content: center;
    padding-left: calc(100vw * (calc(40 / var(--width_base))));
    padding-right: calc(100vw * (calc(40 / var(--width_base))));
    animation: ${fadeOut} ${TEXTO_SALIDA_DURACION}s ease-out ${TEXTO_SALIDA}s
      forwards;
    @media (prefers-reduced-motion: reduce) {
      animation: none;
    }
  `

  /*
   * El bloque mide lo que mide la línea más larga (la 1), así que la línea 2 se
   * centra contra ella. En móvil se topa con el ancho disponible y la línea 1
   * parte en dos renglones sin que eso rompa la escritura.
   */
  const textBlockCss = css`
    width: max-content;
    max-width: 100%;
  `

  const lineCss = css`
    display: block;
    font-family: "Gotham Medium", Gotham, sans-serif;
    font-weight: 600;
    font-style: normal;
    font-size: calc(100vw * (calc(56 / var(--width_base))));
    line-height: 1.45;
    color: ${COLOR_TEXTO};
    @media (max-width: 767px) {
      font-size: calc(100vw * (calc(28 / var(--width_base))));
    }
  `

  const linea1Css = css`
    ${lineCss};
    text-align: left;
    /*
     * En móvil la frase no entra en un renglón a un tamaño legible, así que se
     * parte en dos y se centra, para que acompañe a la caja centrada de la
     * línea 2. El ancho sigue reservado, así que la escritura tampoco se mueve.
     */
    @media (max-width: 767px) {
      text-align: center;
    }
  `

  /*
   * El renglón solo centra la caja; el ancho lo pone la caja, no él.
   */
  const linea2Css = css`
    ${lineCss};
    text-align: center;
  `

  /*
   * La caja mide exactamente lo que medirá la frase completa y va centrada bajo
   * la línea 1. Ese ancho lo fija el fantasma, que es la frase entera puesta en
   * el flujo pero invisible; encima va el texto real pegado al borde derecho de
   * la caja. Así cada letra nueva nace en la derecha y empuja a las anteriores
   * hacia la izquierda, sin que la caja cambie de ancho ni se mueva de sitio.
   */
  const linea2CajaCss = css`
    position: relative;
    display: inline-block;
    white-space: nowrap;
  `

  const linea2FantasmaCss = css`
    visibility: hidden;
  `

  const linea2TextoCss = css`
    position: absolute;
    top: 0;
    right: 0;
    white-space: nowrap;
  `

  const palabraCss = css`
    display: inline-block;
    white-space: nowrap;
  `

  /* Encendido instantáneo: la letra está o no está, sin fundido */
  const letraCss = delay => css`
    display: inline-block;
    opacity: 0;
    animation: ${fadeIn} 0.01s linear ${delay}s forwards;
    /* Sin movimiento la letra se queda en opacity: 0, que es el estado final */
    @media (prefers-reduced-motion: reduce) {
      animation: none;
    }
  `

  /* ── Ilustración – centrada en el hero ── */

  /*
   * El contenedor se monta siempre, vacío, y el <img> entra recién a los 3.76s.
   * Así el fundido de salida cuelga de la misma línea de tiempo del CSS que el
   * resto (arranca con el primer pintado) y no del momento en que aparece la
   * imagen, que puede correrse si la hidratación llega tarde.
   */
  const dibujoWrapperCss = css`
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: none;
    animation: ${fadeOut} ${DIBUJO_SALIDA_DURACION}s ease-out ${DIBUJO_SALIDA}s
      forwards;
    @media (prefers-reduced-motion: reduce) {
      animation: none;
    }
  `

  const dibujoCss = css`
    width: calc(100vw * (calc(${DIBUJO_ANCHO} / var(--width_base))));
    height: auto;
    object-fit: contain;
    @media (max-width: 767px) {
      width: 86%;
      max-width: none;
    }
  `

  /* ── Imagen – estática, visible desde el inicio ── */

  const imageCss = css`
    display: none;
    height: calc(100vw * (calc(380 / var(--width_base))));
    width: 10%;
    object-fit: contain;
    & img {
      transition: none;
      transform: none;
      object-fit: contain !important;
    }
    @media (max-width: 767px) {
      display: none;
      height: auto;
      width: 100%;
      margin-top: calc(100vw * (calc(80 / var(--width_base))));
    }
  `

  const imageMobileCss = css`
    display: none;
    object-fit: contain;
    margin-top: calc(100vw * (calc(20 / var(--width_base))));
    margin-left: auto;
    margin-right: auto;
    & img {
      transition: none;
      transform: none;
      object-fit: contain !important;
      max-width: 40%;
      margin-left: auto;
      margin-right: auto;
    }
    @media (max-width: 767px) {
      display: none;
    }
  `

  /* ── Render ── */

  const renderPalabra = (palabra, palabraIndex) => (
    <span key={palabraIndex} css={palabraCss}>
      {palabra.map(([letra, delay], letraIndex) => (
        <span key={letraIndex} css={letraCss(delay)}>
          {letra}
        </span>
      ))}
    </span>
  )

  return (
    <SectionBackground
      bgColor={COLOR_FONDO}
      styleCss={sectionCss}
      styleCssContent={sectionContentCss}
      classEle="sectionintro"
    >
      {!sinMovimiento && (
        <div css={textWrapperCss}>
          <div css={textBlockCss}>
            <span css={linea1Css}>
              {palabrasLinea1.map((palabra, index) => (
                <React.Fragment key={index}>
                  {renderPalabra(palabra, index)}{" "}
                </React.Fragment>
              ))}
            </span>
            <span css={linea2Css}>
              <span css={linea2CajaCss}>
                <span css={linea2FantasmaCss}>
                  {recorteLinea2(LINEA_2.length)}
                </span>
                <span css={linea2TextoCss}>{recorteLinea2(letrasLinea2)}</span>
              </span>
            </span>
          </div>
        </div>
      )}
      <div css={dibujoWrapperCss}>
        {(mostrarDibujo || sinMovimiento) && (
          <img
            src={sinMovimiento ? DIBUJO_STILL_SRC : DIBUJO_SRC}
            alt=""
            css={dibujoCss}
          />
        )}
      </div>
      <Image imagen={imagen} styleCss={imageCss} />
      <Image imagen={imagenMobile} styleCss={imageMobileCss} />
    </SectionBackground>
  )
}
